// Componente de ayuda admin.
//
// Trigger: botón "?" en el sidebar (data-tour="help-button"). Al hacer
// click, abre un modal con 2 tabs:
//   - Tour: dispara driver.js con los pasos de la página actual + tour
//     global del sidebar como fallback.
//   - FAQ: lista buscable de preguntas frecuentes (carga desde D1).
//
// Auto-arranca el globalTour una sola vez por user (localStorage flag)
// cuando el componente monta dentro del admin. Si la página actual no
// tiene tour, el botón "Iniciar tour" inicia el globalTour.
//
// Nota: NO usamos el <Modal> compartido. Ya no es por el ancho (el Modal
// acepta maxWidthClass), sino por el layout: este modal tiene header
// sticky + cuerpo scrolleable propio (max-h-88vh flex-col), distinto al
// contenedor p-6 simple del Modal compartido, y ademas interactua con el
// overlay de driver.js al lanzar el tour. Migrarlo implicaria reescribir
// su layout sin beneficio claro (ya cierra con Escape y backdrop).

import { useEffect, useMemo, useRef, useState } from "react";
import { HelpCircle, X, Search, Compass, BookOpen, Loader2 } from "lucide-react";
import { driver, type Driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useLocation } from "react-router-dom";
import {
  driverConfig,
  buildGlobalTour,
  buildTourByPath,
  hasSeenGlobalTour,
  markGlobalTourSeen,
} from "../../lib/tours";
import { adminListFaqs, type FaqRow } from "../../lib/api";
import type { Role } from "@juegossc/shared";

type Tab = "tour" | "faq";

// El componente recibe el rol del padre (AdminLayout) para customizar
// el tour y filtrar FAQs. Mantenerlo como prop (en vez de leer el hook
// adentro) deja el componente puro y fácil de testear.
export function AdminHelp({ role }: { role: Role | null }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("tour");
  const location = useLocation();
  const driverRef = useRef<Driver | null>(null);

  // Auto-tour la primera vez que el user entra a /admin/*. En vez de
  // un setTimeout(600) frágil (cold start / red lenta pueden no llegar
  // a ese plazo y driver.js arrancaba contra selectores que no existían
  // todavía), polleamos hasta que aparezcan los anchors del sidebar.
  // Cap de 3s para no quedarnos esperando para siempre si algo está
  // mal armado.
  useEffect(() => {
    if (hasSeenGlobalTour()) return;
    const ANCHORS = [
      '[data-tour="sidebar-nav"]',
      '[data-tour="help-button"]',
    ];
    const POLL_MS = 50;
    const MAX_MS = 3000;
    let cancelled = false;
    let timeoutId: number | null = null;
    const start = performance.now();
    function check() {
      if (cancelled) return;
      if (ANCHORS.every((s) => document.querySelector(s))) {
        runGlobalTour();
        markGlobalTourSeen();
        return;
      }
      if (performance.now() - start > MAX_MS) return; // timeout silencioso
      timeoutId = window.setTimeout(check, POLL_MS);
    }
    check();
    return () => {
      cancelled = true;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup del driver si el componente se desmonta a mitad de tour
  // (ej: logout). Sin esto queda el overlay flotando.
  useEffect(() => {
    return () => {
      driverRef.current?.destroy();
    };
  }, []);

  function runGlobalTour() {
    driverRef.current?.destroy();
    const d = driver({ ...driverConfig, steps: buildGlobalTour(role) });
    driverRef.current = d;
    d.drive();
  }

  function runPageTour() {
    setOpen(false);
    // El modal cierra inmediato; pequeño delay para que el overlay
    // del modal NO interfiera con el highlight del driver.
    setTimeout(() => {
      const steps = buildTourByPath(location.pathname, role);
      if (!steps || steps.length === 0) {
        // Fallback al tour global si la página no tiene uno propio.
        runGlobalTour();
        return;
      }
      driverRef.current?.destroy();
      const d = driver({ ...driverConfig, steps });
      driverRef.current = d;
      d.drive();
    }, 150);
  }

  const hasPageTour = !!buildTourByPath(location.pathname, role)?.length;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-tour="help-button"
        aria-label="Abrir ayuda"
        className="fixed bottom-4 left-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-[#a78bfa] to-[#7c3aed] text-white shadow-lg ring-1 ring-white/20 transition hover:scale-105 hover:shadow-xl md:bottom-5 md:left-5 md:h-14 md:w-14"
      >
        <HelpCircle className="h-6 w-6" />
      </button>

      {open && (
        <HelpModal
          tab={tab}
          onTab={setTab}
          onClose={() => setOpen(false)}
          onStartTour={runPageTour}
          pathHasTour={hasPageTour}
          currentPath={location.pathname}
          role={role}
        />
      )}
    </>
  );
}

function HelpModal({
  tab,
  onTab,
  onClose,
  onStartTour,
  pathHasTour,
  currentPath,
  role,
}: {
  tab: Tab;
  onTab: (t: Tab) => void;
  onClose: () => void;
  onStartTour: () => void;
  pathHasTour: boolean;
  currentPath: string;
  role: Role | null;
}) {
  // Bloquear scroll del body mientras está abierto.
  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = orig;
    };
  }, []);

  // Cerrar con Escape — atajo estándar de modales.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-[#1a1540] shadow-2xl">
        {/* Header con tabs */}
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
          <div className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-[#c4b5fd]" />
            <h2 className="text-xl font-bold">Ayuda</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            className="rounded-lg p-1.5 opacity-70 hover:bg-white/10 hover:opacity-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 px-6">
          <TabBtn
            active={tab === "tour"}
            onClick={() => onTab("tour")}
            icon={<Compass className="h-4 w-4" />}
          >
            Tour guiado
          </TabBtn>
          <TabBtn
            active={tab === "faq"}
            onClick={() => onTab("faq")}
            icon={<BookOpen className="h-4 w-4" />}
          >
            Preguntas frecuentes
          </TabBtn>
        </div>

        {/* Contenido */}
        <div className="flex-1 overflow-y-auto p-6">
          {tab === "tour" ? (
            <TourPanel
              pathHasTour={pathHasTour}
              onStart={onStartTour}
              currentPath={currentPath}
            />
          ) : (
            <FaqPanel role={role} />
          )}
        </div>
      </div>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition ${
        active
          ? "border-[#a78bfa] text-[#c4b5fd]"
          : "border-transparent opacity-60 hover:opacity-100"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

function TourPanel({
  pathHasTour,
  onStart,
  currentPath,
}: {
  pathHasTour: boolean;
  onStart: () => void;
  currentPath: string;
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm opacity-80">
        El tour guiado te muestra los elementos principales de la página
        actual, resaltándolos uno por uno con una breve explicación.
      </p>

      <div className="rounded-lg bg-white/5 p-4">
        <p className="text-xs uppercase tracking-wider opacity-50">
          Página actual
        </p>
        <p className="mt-1 font-mono text-sm text-[#c4b5fd]">{currentPath}</p>
        {!pathHasTour && (
          <p className="mt-2 text-xs opacity-70">
            Esta página no tiene un tour propio todavía. Vamos a hacer el tour
            general del panel.
          </p>
        )}
      </div>

      <button
        onClick={onStart}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] py-3 font-bold transition hover:opacity-90"
      >
        <Compass className="h-4 w-4" />
        {pathHasTour ? "Iniciar tour de esta página" : "Iniciar tour general"}
      </button>

      <p className="text-center text-xs opacity-50">
        Podés cancelar en cualquier momento con Esc o haciendo click afuera.
      </p>
    </div>
  );
}

const CATEGORIA_LABELS: Record<string, string> = {
  general: "General",
  productos: "Productos",
  juegos: "Juegos",
  sucursales: "Sucursales",
  super_admin: "Super admin",
};

function FaqPanel({ role }: { role: Role | null }) {
  const [faqs, setFaqs] = useState<FaqRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [openIds, setOpenIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    adminListFaqs()
      .then(setFaqs)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  // Filtro por rol: las FAQs con categoría 'super_admin' (o cualquier
  // categoría que conceptualmente sea de páginas super-only) no las
  // ve un admin común — no son ni accesibles ni relevantes para él.
  // Importante: este filtro es UX (defensa en frontend); las FAQs son
  // públicas en el endpoint /api/admin/faqs, pero el contenido típico
  // no es sensible — son tips de uso, no secretos.
  const visibles = useMemo(() => {
    if (!faqs) return [];
    if (role === "super_admin") return faqs;
    return faqs.filter((f) => f.categoria !== "super_admin");
  }, [faqs, role]);

  // Búsqueda case-insensitive sobre pregunta + respuesta + categoría.
  // Si query vacío, muestra todo. Filtro en memoria — esperamos pocas
  // FAQs (< 100) entonces no hace falta paginar / index full-text.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return visibles;
    return visibles.filter(
      (f) =>
        f.pregunta.toLowerCase().includes(q) ||
        f.respuesta.toLowerCase().includes(q) ||
        f.categoria.toLowerCase().includes(q),
    );
  }, [visibles, query]);

  // Agrupado por categoría — más legible cuando hay muchas FAQs.
  const grouped = useMemo(() => {
    const out: Record<string, FaqRow[]> = {};
    for (const f of filtered) {
      if (!out[f.categoria]) out[f.categoria] = [];
      out[f.categoria].push(f);
    }
    return out;
  }, [filtered]);

  function toggle(id: number) {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-4">
      {/* Buscador siempre visible — patrón estándar de FAQ. */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar… (ej: stock, modo prueba, memoria)"
          className="w-full rounded-lg bg-white/10 px-3 py-2 pl-10 outline-none focus:ring-2 focus:ring-[#a78bfa]"
          autoFocus
        />
      </div>

      {error && (
        <div className="rounded-lg bg-red-500/15 p-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {!faqs && !error && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin opacity-50" />
        </div>
      )}

      {faqs && filtered.length === 0 && (
        <p className="py-8 text-center text-sm opacity-60">
          {query.trim().length > 0
            ? `No hay resultados para "${query}".`
            : "No hay preguntas cargadas todavía."}
        </p>
      )}

      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat} className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[#c4b5fd]">
            {CATEGORIA_LABELS[cat] ?? cat}
          </h3>
          {items.map((f) => (
            <FaqItem
              key={f.id}
              entry={f}
              open={openIds.has(f.id)}
              onToggle={() => toggle(f.id)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function FaqItem({
  entry,
  open,
  onToggle,
}: {
  entry: FaqRow;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="overflow-hidden rounded-lg bg-white/5">
      <button
        onClick={onToggle}
        className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left transition hover:bg-white/5"
      >
        <span className="text-sm font-medium">{entry.pregunta}</span>
        <span
          className={`mt-0.5 shrink-0 text-xs opacity-50 transition-transform ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden
        >
          ▼
        </span>
      </button>
      {open && (
        <div className="border-t border-white/10 px-4 py-3 text-sm opacity-90">
          {/* Respuesta como texto simple con saltos de línea preservados.
              No renderizamos markdown todavía — si después se quiere,
              cambiar a react-markdown. */}
          <p className="whitespace-pre-wrap">{entry.respuesta}</p>
        </div>
      )}
    </div>
  );
}
